import {
  Button,
  HStack,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  Avatar,
  Text,
  Heading,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  SimpleGrid,
} from "@chakra-ui/react"
//import { Api } from "../utils/api"
import React from "react"
import TeamEditor from "./TeamEditor"
import MemberList from "./MemberList"
import { addAlert, AlertType } from "@/lib/alerts"
import { Schemas, CurrentOrgId } from "@/lib/store"
import { useParams } from "react-router-dom"
import { AddIcon, MinusIcon, CheckIcon, WarningTwoIcon } from "@chakra-ui/icons"
import { Api } from "@/lib/api"

/**
 * @param {Object} teams - The teams to display.
 * @param {Object} members - The members to display.
 * @param {React.ReactNode} membersTopToolbar - Optional toolbar to display above the members list.
 * @param {React.ReactNode} membersBottomToolbar - Optional toolbar to display below the members list.
 * @param {React.ReactNode} teamsTopToolbar - Optional toolbar to display above the teams list.
 * @param {React.ReactNode} teamsBottomToolbar - Optional toolbar to display below the teams list.
 * @returns {React.ReactNode} The rendered component.
 */
const TeamManager = ({
  emptyState,
  members,
  teams,
  membersTopToolbar,
  membersBottomToolbar,
  teamsTopToolbar,
  teamsBottomToolbar,
  onSuccessfulSaveAndClose,
}) => {
  const [masterTeams, setMasterTeams] = React.useState([])
  const [editedTeams, setEditedTeams] = React.useState([])
  const [selectedTeam, setSelectedTeamId] = React.useState(null)
  const [currentOrgId] = CurrentOrgId.useState()
  const [isOpen, setIsOpen] = React.useState(false)
  const api = new Api()
  const [schemas] = Schemas.useState()
  const { schemaId } = useParams()
  // Find the selected schema
  const schemaName = React.useMemo(
    () => schemas[schemaId]?.name,
    [schemas, schemaId],
  )

  const [memberState, setMemberState] = React.useState([])
  const [memberStatuses, setMemberStatuses] = React.useState({})

  const [changes, setChanges] = React.useState([])

  const [isSaved, setIsSaved] = React.useState(false)

  const getTeamChanges = () => {
    return editedTeams
      .map((editedTeam) => {
        const originalTeam = masterTeams.find(
          (team) => team.id === editedTeam.id,
        )

        // Find added and removed members
        const addedMembers = editedTeam.members.filter(
          (member) =>
            !originalTeam.members.some(
              (origMember) => origMember.id === member.id,
            ),
        )
        const removedMembers = originalTeam.members.filter(
          (member) =>
            !editedTeam.members.some(
              (editedMember) => editedMember.id === member.id,
            ),
        )

        // Only return teams that have changes
        if (addedMembers.length === 0 && removedMembers.length === 0) {
          return null
        }

        return {
          ...editedTeam,
          addedMembers,
          removedMembers,
        }
      })
      .filter(Boolean) // Remove null entries
  }

  const hasChanges = React.useMemo(() => {
    return getTeamChanges().length > 0
  }, [editedTeams, masterTeams])

  React.useEffect(() => {
    setMasterTeams(teams)
    setEditedTeams(teams)
  }, [teams])

  React.useEffect(() => {
    setMemberState(members)
  }, [members])

  React.useEffect(() => {
    console.log("selectedTeam", selectedTeam)
    console.log("editedTeams", editedTeams)
    console.log("members", members)
  }, [selectedTeam])

  React.useEffect(() => {
    if (isOpen) {
      setChanges(getTeamChanges())
    }
  }, [isOpen])

  const handleCancel = () => {
    setEditedTeams(masterTeams)
    const resetMembers = memberState.map((member) => {
      const memberTeams = masterTeams
        .filter((team) =>
          team.members.some((teamMember) => teamMember.id === member.id),
        )
        .map((team) => team.id)

      return {
        ...member,
        in_team: memberTeams,
      }
    })
    setMemberState(resetMembers)
  }

  const handleAddMember = (member) => {
    setIsSaved(false)
    setMemberState((prevMembers) =>
      prevMembers.map((m) => {
        if (m.id === member.id) {
          return {
            ...m,
            in_team: [...m.in_team, selectedTeam],
          }
        }
        return m
      }),
    )

    setEditedTeams((prevTeams) =>
      prevTeams.map((team) => {
        if (team.id === selectedTeam) {
          return {
            ...team,
            members: [...team.members, member],
          }
        }
        return team
      }),
    )
  }

  const handleRemoveMember = (member) => {
    setIsSaved(false)
    if (
      !member.in_team.some((teamId) => String(teamId) === String(selectedTeam))
    ) {
      console.log("Team key not found, cannot remove member")
      addAlert(AlertType.WARNING, "Cannot Remove User", "Member not in team", 5)
      return
    }

    setMemberState((prevMembers) =>
      prevMembers.map((m) => {
        if (m.id === member.id) {
          return {
            ...m,
            in_team: m.in_team.filter(
              (teamId) => String(teamId) !== String(selectedTeam),
            ),
          }
        }
        return m
      }),
    )

    setEditedTeams((prevTeams) =>
      prevTeams.map((team) => {
        if (team.id === selectedTeam) {
          return {
            ...team,
            members: team.members.filter((m) => m.id !== member.id),
          }
        }
        return team
      }),
    )
  }

  const confirmSave = () => {
    setIsOpen(true)
  }

  const onClose = () => {
    if (isSaved) {
      onSuccessfulSaveAndClose?.()
    }
    setMemberStatuses({})
    setIsOpen(false)
  }

  const handleSave = async () => {
    const failedOperations = []

    let successBody = []

    let sucesses = 0
    let fails = 0
    for (const team of changes) {
      // Handle member additions
      let addedSucesses = sucesses
      let addedFails = fails
      for (const member of team.addedMembers) {
        try {
          await api.teamsAddMember(currentOrgId, team.id, member.id)
          addedSucesses++
          setMemberStatuses((prev) => ({
            ...prev,
            [`${team.id}-${member.id}`]: "pending-success",
          }))
          // Set a timeout to update the status to 'success' after 3 seconds
          setTimeout(() => {}, 3000)
        } catch (err) {
          failedOperations.push(
            "Failed to add member " +
              member.email +
              " to team " +
              team.name +
              ": " +
              err.message,
          )
          addedFails++
          console.log(
            "Failed to add member",
            member.email,
            "error",
            err.message,
          )
          setMemberStatuses((prev) => ({
            ...prev,
            [`${team.id}-${member.id}`]: "error",
          }))
        }
      }
      let removedSucesses = 0
      let removedFails = 0
      // Handle member removals
      for (const member of team.removedMembers) {
        try {
          await api.teamsRemoveMember(currentOrgId, team.id, member.id)
          removedSucesses++
          setMemberStatuses((prev) => ({
            ...prev,
            [`${team.id}-${member.id}`]: "pending-success",
          }))
          // Set a timeout to update the status to 'success' after 3 seconds
          setTimeout(() => {
            setMemberStatuses((prev) => ({
              ...prev,
              [`${team.id}-${member.id}`]: "success",
            }))
          }, 3000)
        } catch (err) {
          removedFails++
          failedOperations.push(
            "Failed to remove member " +
              member.email +
              " from team " +
              team.name +
              ": " +
              err.message,
          )
          console.log(
            "Failed to remove member",
            member.email,
            "error",
            err.message,
          )
          setMemberStatuses((prev) => ({
            ...prev,
            [`${team.id}-${member.id}`]: "error",
          }))
        }
        if (addedSucesses > 0) {
          successBody.push(
            team.name + " successfully added " + addedSucesses + " members",
          )
        }
        if (removedSucesses > 0) {
          successBody.push(
            team.name + " successfully removed " + removedSucesses + " members",
          )
        }
      }
      sucesses = addedSucesses + removedSucesses
      fails = addedFails + removedFails
    }
    if (sucesses > 0) {
      let timer = 5
      if (failedOperations.length > 1) {
        timer = 0
      }
      addAlert(
        AlertType.SUCCESS,
        "Team Permissions Saved",
        <VStack>
          {Array.isArray(successBody) ? (
            successBody.map((body) => <Text key={body}>{body}</Text>)
          ) : (
            <Text>{successBody}</Text>
          )}
        </VStack>,
        timer,
      )
      // Call the refresh callback if provided
      setIsSaved(true)
    }
    if (fails > 0) {
      addAlert(
        AlertType.ERROR,
        "Team Permissions Save Failed",
        failedOperations.join("\n"),
      )
    }
    if (sucesses > 0 && fails > 0) {
      addAlert(
        AlertType.WARNING,
        "Team Permissions Save Failed",
        failedOperations.join("\n"),
      )
    }
  }

  const confirmModal = () => {
    const changes = getTeamChanges()
    return (
      <Accordion
        variant="permissions"
        allowToggle
        defaultIndex={changes.map((_, index) => index)}
      >
        <VStack spacing={1} width="100%">
          {changes.map((team) => (
            <>
              <AccordionItem key={team.id}>
                <AccordionButton margin="0em">
                  <Heading size="sm">{team.name}</Heading>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack width="100%">
                    {team.addedMembers.map((member) => {
                      const status = memberStatuses[`${team.id}-${member.id}`]
                      return (
                        <HStack
                          width="100%"
                          key={`added-${member.id}`}
                          justifyContent="space-between"
                        >
                          <HStack>
                            <Avatar
                              bg="Primary.500"
                              height="1.25em"
                              width="1.25em"
                            />
                            <Text>{member.email}</Text>
                          </HStack>
                          <Box
                            bg={
                              status === "success" ||
                              status === "pending-success"
                                ? "green.300"
                                : status === "error"
                                  ? "red.500"
                                  : "blue.300"
                            }
                            borderRadius="full"
                            height="1.25em"
                            width="1.25em"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {status === "success" ||
                            status === "pending-success" ? (
                              <CheckIcon color="white" boxSize="0.6em" />
                            ) : status === "error" ? (
                              <WarningTwoIcon color="white" boxSize="0.6em" />
                            ) : (
                              <AddIcon color="white" boxSize="0.6em" />
                            )}
                          </Box>
                        </HStack>
                      )
                    })}
                    {team.removedMembers.map((member) => {
                      const status = memberStatuses[`${team.id}-${member.id}`]
                      return (
                        <HStack
                          width="100%"
                          key={`removed-${member.id}`}
                          justifyContent="space-between"
                        >
                          <HStack>
                            <Avatar
                              bg="Primary.500"
                              height="1.25em"
                              width="1.25em"
                            />
                            <Text>{member.email}</Text>
                          </HStack>
                          <Box
                            bg={
                              status === "success" ||
                              status === "pending-success"
                                ? "green.300"
                                : status === "error"
                                  ? "red.500"
                                  : "red.300"
                            }
                            borderRadius="full"
                            height="1.25em"
                            width="1.25em"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {status === "success" ||
                            status === "pending-success" ? (
                              <CheckIcon color="white" boxSize="0.6em" />
                            ) : status === "error" ? (
                              <WarningTwoIcon color="white" boxSize="0.6em" />
                            ) : (
                              <MinusIcon color="white" boxSize="0.6em" />
                            )}
                          </Box>
                        </HStack>
                      )
                    })}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </>
          ))}
        </VStack>
      </Accordion>
    )
  }

  return (
    <>
      <VStack width="100%" height="100%">
        <SimpleGrid
          p={4}
          height="100%"
          width="100%"
          padding="0"
          spacing="1em"
          columns={{ base: 1, md: 2 }}
        >
          {emptyState !== null && emptyState ? (
            emptyState
          ) : (
            <MemberList
              handleAddMember={handleAddMember}
              handleRemoveMember={handleRemoveMember}
              membersTopToolbar={membersTopToolbar}
              membersBottomToolbar={membersBottomToolbar}
              members={memberState}
              selectedTeamId={selectedTeam}
            />
          )}
          <TeamEditor
            handleRemoveMember={handleRemoveMember}
            setSelectedTeamId={setSelectedTeamId}
            teamsTopToolbar={teamsTopToolbar}
            teamsBottomToolbar={teamsBottomToolbar}
            teams={teams}
            editedTeams={editedTeams}
            setEditedTeams={setEditedTeams}
          />
        </SimpleGrid>
        <HStack
          colspan={2}
          width="90%"
          bg="white"
          borderRadius="0.75em"
          boxShadow="lg"
          marginTop="1em"
          padding="0.25em"
        >
          <Button ml="auto" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmSave}
            isDisabled={!hasChanges}
          >
            Save
          </Button>
        </HStack>
      </VStack>
      <Modal isOpen={isOpen} onClose={onClose} variant="dataUpload">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader flexWrap="wrap">
            {schemaName + " Permissions Changes"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p="2em">{confirmModal()}</ModalBody>
          <ModalFooter>
            <HStack justifyContent="space-between" width="100%">
              <HStack>
                <Button variant="ghost" onClick={onClose} isDisabled={isSaved}>
                  Edit
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleSave()}
                  isDisabled={isSaved}
                >
                  Save
                </Button>
              </HStack>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default TeamManager
